const _globalQueues = {};

/**
 * The 'enqueue' function provides access to a global dictionary of EnQueue objects.
 * The underlying functionality is provided by the EnQueue class.
 * 
 * En-queueing stuff is useful when you have code that is itself asynchronous but you need a way to serialize their
 * execution. If your async code is all within a single code context then you can usually just manage it yourself
 * using promises/etc. However, if your code context doesn't allow you access to each instance/execution then this
 * enqueue code can help by providing a global queuing mechanism.
 *
 * Tasks will be executed non-concurrently (serialized), also a special Promise will be returned that will only
 * resolve once the queue has been exhausted (no more tasks remain), allowing you to execute additional code once
 * your tasks have completed. Note that the returned promise is reset (new) once the queue is exhausted, this is
 * because a queue can be reused later/immediately.
 * 
 * EX SCENARIO:
 * You have a country-chooser component, it needs to load the list of countries but only needs to do so once per app load,
 * not for every instance of the component. It can enqueue its load countries function, in-which it would check if the
 * shared countries list has already been loaded and only requesting them from server if they haven't already.
 * Then, since each instance will enqueue the load function, each will execute in sequence/serialized and only the first
 * will actually load from server, the remaining executions will see their shared countries list has already been loaded.
 * 
 * @param {string} queueKey - A lookup key (string) into the global lookup of queues.
 * @param {!Function} task - The function to execute when its turn is up.
 * @returns {EnQueue} - An EnQueue object that can be added to.
 */
function enqueue(queueKey, task) {
    if (typeof queueKey !== 'string') throw new Error('enqueue: queueKey must be a String');
    if (typeof task !== 'function') throw new Error('enqueue: task must be a Function');

    // Get an existing EnQueue object from the global queues by key lookup,
    // if one doesn't exist then create a new EnQueue object and set into the global queues.
    const queue = _globalQueues[queueKey] = _globalQueues[queueKey] || new EnQueue();

    // Return the EnQueue (returned from add) to allow chaining and calling the 'then' method.
    return queue.add(task);
}

class EnQueue {
    constructor() {
        this._queue = [];
        this._promiseDone = null;
        this._promiseDoneResolver = null;
    }

    /**
     * Add a new task to the queue. If no tasks are currently queued then the new task will start immediately.
     * Any tasks added while there are queued tasks executing will simply be added to the queue and will be executed in order (FIFO).
     *
     * Task functions will be passed a single argument with the return value from the previously executed task (if there was one).
     * 
     * @param {Function} task - The function to execute in FIFO order.
     * @returns {Promise} - A promise that will resolve once the queue has been exhausted (emptied). The result value will be the return value of the last task.
     */
    add(task) {
        // If an array is provided then recursively iterate through it.
        if (Array.isArray(task)) {
            if (task.length) {
                this.add(task.shift()); // Add the first in the array.
                this.add(task); // Continue with the rest in the array.
            }
            return;
        }

        if (!(task instanceof Function)) {
            throw new Error('EnQueue.add: Invalid task type, must be a Function or an array of Functions.');
        }

        this._add(task);
        return this._start();
    }

    _add(task) {
        // Add the task to the queue, but first we wrap it in a promise which resolves when the task finishes,
        // and wrap the creation/execution of the promise in a function so it doesn't execute immediately.
        // The wrapper function will be executed when it's turn is up, and the promise & task will then be run.
        // The _next function which executes the queued tasks will receive the returned promise and will wait
        // for it to be done before executing the next task in the queue.
        this._queue.push((prevValue) => new Promise(res => {
            const val = task(prevValue); // The previously executed task's return value (if there was one) is passed in, allowing the task to optionally use it if desired.

            // If task returned a promise then wait for it to finish before resolving our promise,
            // otherwise just resolve with the returned value.
            if (val instanceof Promise)
                val.then(resultVal => res(resultVal));
            else
                res(val);
        }));
    }

    _start() {
        if (!this._queue.length)
            throw new Error('EnQueue._init: !queue.length');

        // There is only 1 task in the queue, this means we've just started a queue
        // session, so we need to initialize and start the queue execution.
        if (this._queue.length === 1) {
            // Initialize the promise that will be executed once the queue has been exhausted.
            this._promiseDone = new Promise(res => {
                this._promiseDoneResolver = res;
            });

            // Start queue execution.
            this._next();
        }
        // Else if more than one task is already in the queue then we should already be started.

        return this._promiseDone;
    }

    _next(prevValue) {
        // No tasks left, we're done (for this queueing 'session').
        if (!this._queue.length) {
            // Resolve the 'done' promise, which external code may have then'd.
            this._promiseDoneResolver(prevValue); // Resolve with whatever the last task provided as return value.
            return;
        }

        // Run the next task (which is wrapped in a promise), pass in the previously executed task's return value (so it can optionally use it).
        this._queue[0](prevValue)
            .then(val => {
                this._queue.shift(); // Remove the task we just completed.
                this._next(val); // Run the next task, passing in the result value of the task that just ran.
            });
    }
}

export { enqueue, EnQueue };